import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import {
  Button,
  Drawer,
  Input,
  message,
  Popconfirm,
  Skeleton,
  Switch,
} from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { validateEmail } from '../../utils/email';
import { validatePassword } from '../../utils/password';
import { validatePhone } from '../../utils/phone';
import { sanitizeObject } from '../../utils/object';
import {
  ClientEmployeeQuery,
  ClientEmployeesDocument,
  ClientEmployeesQuery,
  ClientEmployeesQueryVariables,
  UpdateClientEmployeeMutationVariables,
  useClientEmployeeQuery,
  useDeleteClientEmployeeMutation,
  useUpdateClientEmployeeMutation,
} from '../../graphql/datamodel.gen';
import { getClient } from '../../apollo';
import { cloneDeep } from 'lodash';
import firebase from 'firebase';

interface UpdateEmployeeDrawerProps {
  visible: boolean;
  onClose: () => any;
  employeeId: string;
}

interface FormProps extends FormComponentProps {
  employee: ClientEmployeeQuery['clientEmployee'];
  onSubmit?: (input: UpdateClientEmployeeMutationVariables) => Promise<any>;
  onDelete?: (id: string) => Promise<boolean>;
}
interface FormState {
  updating: boolean;
  loginEnabled: boolean;
}

interface FormValues {
  name: string;
  email: string;
  phone?: string;
}

class UpdateEmployeeForm extends React.Component<FormProps, FormState> {
  state = {
    updating: false,
    loginEnabled: false,
  };

  constructor(props: Readonly<FormProps>) {
    super(props);

    this.state = {
      updating: false,
      loginEnabled: this.props.employee.loginEnabled,
    };
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values: FormValues) => {
      if (!err) {
        const sanitizedValues = sanitizeObject<FormValues>(values);

        try {
          message.loading({
            content: 'Actualizando empleado..',
            key: 'update_employee',
          });
          this.setState({
            updating: true,
          });

          await this.props.onSubmit({
            id: this.props.employee.id,
            data: {
              disableLogin: !this.state.loginEnabled,
              email: sanitizedValues.email,
              name: sanitizedValues.name,
              phone: sanitizedValues.phone,
            },
          });

          message.success({
            content: 'Hecho!',
            key: 'update_employee',
          });
        } catch (e) {
          message.error({
            content: 'Error actualizando empleado',
            key: 'update_employee',
          });
        } finally {
          this.setState({
            updating: false,
          });
          setTimeout(() => {
            message.destroy();
          }, 4000);
        }
      }
    });
  };

  emailValidator = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    const invalid = await validateEmail(value, {
      isRequired: { enabled: true },
      checkAgainstFirebase: {
        enabled: true,
        message: 'Ya existe un empleado con ese email',
        previousEmail: this.props.employee.email,
        newEmail: value,
      },
    });

    if (invalid) return callback(invalid);
    return callback();
  };

  passwordValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePassword(value, {
      required: true,
    });
    if (invalid) return callback(invalid);
    return callback();
  };

  phoneValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePhone(value, {
      isRequired: {
        enabled: false,
      },
    });
    if (invalid) {
      return callback(invalid);
    }
    return callback();
  };

  deleteEmployee = async () => {
    try {
      message.loading({
        content: 'Eliminando empleado..',
        key: 'delete_employee',
      });

      const deleted = await this.props.onDelete(this.props.employee.id);

      if (deleted) {
        message.success({
          content: 'Hecho!',
          key: 'delete_employee',
        });
      } else {
        message.error({
          content: 'Error eliminando empleado',
          key: 'delete_employee',
        });
      }
    } catch (e) {
      message.error({
        content: 'Error eliminando empleado',
        key: 'update_employee',
      });
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 4000);
    }
  };

  sendPasswordResetEmail = async () => {
    try {
      message.loading({
        content: 'Enviando email de restablecimiento de contraseña..',
        key: 'send_pwd_reset',
      });
      await firebase.auth().sendPasswordResetEmail(this.props.employee.email);
      message.success({
        content: 'Hecho!',
        key: 'send_pwd_reset',
      });
    } catch (e) {
      message.error({
        content: 'Error el enviar correo',
        key: 'send_pwd_reset',
      });
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 4000);
    }
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    return (
      <Form id={'update-employee-form'} onSubmit={this.handleSubmit}>
        <Form.Item label={'Nombre'} required={false}>
          {getFieldDecorator('name', {
            initialValue: this.props.employee.name,
            rules: [
              {
                required: true,
                message: 'No puedes dejar el nombre en blanco',
              },
              {
                whitespace: true,
                message:
                  'El nombre no puede tener todos los carácteres en blanco',
              },
            ],
          })(<Input placeholder={'Introduce un nombre'} />)}
        </Form.Item>
        <Form.Item label={'Email'} required={false}>
          {getFieldDecorator('email', {
            initialValue: this.props.employee.email,
            rules: [
              {
                required: true,
                message: 'No puedes dejar el email en blanco',
              },
              {
                validator: this.emailValidator,
              },
            ],
          })(<Input disabled={false} />)}
        </Form.Item>
        <Form.Item label={'Teléfono'} required={false}>
          {getFieldDecorator('phone', {
            initialValue: this.props.employee.phone,
            rules: [
              {
                validator: this.phoneValidator,
              },
              {
                whitespace: true,
                message:
                  'El teléfono no puede tener todos los carácteres en blanco',
              },
            ],
          })(<Input placeholder={'Puedes introducir un teléfono'} />)}
        </Form.Item>
        <Form.Item label={'Acceso a Web Panel'} required={false}>
          <Switch
            onClick={(checked) => {
              this.setState({
                loginEnabled: checked,
              });
            }}
            checkedChildren="Sí"
            unCheckedChildren="No"
            defaultChecked={this.props.employee.loginEnabled}
          />
        </Form.Item>

        <Form.Item>
          <a
            style={{ float: 'right', lineHeight: 1.3, textAlign: 'right' }}
            onClick={this.sendPasswordResetEmail}
          >
            Enviar email de restablecimiento de contraseña
          </a>
        </Form.Item>

        <Form.Item>
          <a className="login-form-delete" href="">
            <Popconfirm
              placement={'topLeft'}
              title="Seguro que quieres eliminar este empleado?"
              okText="Si"
              cancelText="No"
              onConfirm={this.deleteEmployee}
            >
              Eliminar empleado
            </Popconfirm>
          </a>
        </Form.Item>
        <Form.Item>
          <Button
            loading={this.state.updating}
            type="primary"
            htmlType="submit"
            className="login-form-button"
          >
            Guardar cambios
          </Button>
        </Form.Item>
      </Form>
    );
  }
}

const MyUpdateEmployeeForm = Form.create<FormProps>()(UpdateEmployeeForm);

const Container: React.FC<UpdateEmployeeDrawerProps> = (
  props: UpdateEmployeeDrawerProps,
) => {
  const { loading: employeeLoading, data: dataEmployee } =
    useClientEmployeeQuery({
      client: getClient(),
      fetchPolicy: 'cache-and-network',
      variables: {
        id: props.employeeId,
      },
    });

  const [updateClientEmployeeMut] = useUpdateClientEmployeeMutation({
    client: getClient(),
  });

  const [deleteClientEmployeeMut] = useDeleteClientEmployeeMutation({
    client: getClient(),
  });

  const employee = dataEmployee?.clientEmployee;

  const updateClientEmployee = (
    input: UpdateClientEmployeeMutationVariables,
  ) => {
    return updateClientEmployeeMut({
      variables: input,
    });
  };

  const deleteClientEmployee = async (id: string): Promise<boolean> => {
    try {
      const result = await deleteClientEmployeeMut({
        variables: {
          id,
        },
        update: (proxy, mutationResult) => {
          if (mutationResult.data?.deleteClientEmployee?.deleted) {
            const graphqlClient = getClient();

            try {
              const previousEmployees = cloneDeep(
                graphqlClient.readQuery<
                  ClientEmployeesQuery,
                  ClientEmployeesQueryVariables
                >({
                  query: ClientEmployeesDocument,
                }),
              );

              const updatedEmployeeList =
                previousEmployees.clientEmployees.filter(
                  (employee) => employee.id !== id,
                );

              graphqlClient.writeQuery({
                query: ClientEmployeesDocument,
                data: {
                  clientEmployees: updatedEmployeeList,
                },
              });

              // @ts-ignore
              graphqlClient.queryManager.broadcastQueries();
            } catch (e) {}
          }
        },
      });

      if (result.data?.deleteClientEmployee?.deleted) {
        props.onClose();
      }

      return result.data?.deleteClientEmployee?.deleted;
    } catch (e) {
      return false;
    }
  };

  return (
    <>
      {employeeLoading && <Skeleton active={true} loading={true} />}
      {props.visible && !employeeLoading && employee && (
        <MyUpdateEmployeeForm
          employee={employee}
          onSubmit={updateClientEmployee}
          onDelete={deleteClientEmployee}
        />
      )}
    </>
  );
};

const UpdateEmployeeDrawer: React.FC<UpdateEmployeeDrawerProps> = (
  props: UpdateEmployeeDrawerProps,
) => {
  return (
    <Drawer
      closable={true}
      destroyOnClose={true}
      width={300}
      maskClosable={true}
      title={'Gestionar empleado'}
      visible={props.visible}
      onClose={props.onClose}
    >
      {props.visible && props.employeeId && <Container {...props} />}
    </Drawer>
  );
};

export default UpdateEmployeeDrawer;
